/**
 * Task 26: Admin Dashboard with User Management
 * Tabs: Overview, Create User, Bulk Upload, Pending Approvals, Audit Log
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, UserPlus, Upload, Clock, FileText, CheckCircle, XCircle, 
  AlertTriangle, Mail, MapPin, Phone, Shield, Trash2, UserX
} from 'lucide-react';
import axiosInstance from '../config/axiosConfig';
import UserUploadModal from '../components/UserUploadModal';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // User editing state
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    role: '',
    manager_id: null
  });

  // Create User Form State
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    role: 'recruiter',
    mobile: '',
    location: ''
  });

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
    fetchPendingApprovals();
    fetchAuditLogs();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axiosInstance.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await axiosInstance.get('/users/pending-approvals');
      setPendingUsers(response.data);
    } catch (error) {
      console.error('Error fetching pending users:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await axiosInstance.get('/users/audit?limit=50');
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/users/create', createForm);
      setToast({ message: 'User created successfully!', type: 'success' });
      setCreateForm({ name: '', email: '', role: 'recruiter', mobile: '', location: '' });
      fetchUsers();
      fetchPendingApprovals();
    } catch (error) {
      setToast({ message: error.response?.data?.detail || 'Failed to create user', type: 'error' });
    }
  };

  const handleApproval = async (userId, approved) => {
    try {
      await axiosInstance.patch('/users/approve', { user_id: userId, approved });
      setToast({ message: `User ${approved ? 'approved' : 'rejected'} successfully!`, type: 'success' });
      fetchUsers();
      fetchPendingApprovals();
      fetchAuditLogs();
    } catch (error) {
      setToast({ message: 'Failed to update approval status', type: 'error' });
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user.id);
    setEditForm({
      role: user.role,
      manager_id: user.manager_id || 0
    });
  };

  const handleSaveUser = async (userId) => {
    try {
      await axiosInstance.patch(`/users/${userId}`, editForm);
      setToast({ message: 'User updated successfully!', type: 'success' });
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      setToast({ message: error.response?.data?.detail || 'Failed to update user', type: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({ role: '', manager_id: null });
  };

  const getManagerName = (managerId) => {
    if (!managerId) return '—';
    const manager = users.find(u => u.id === managerId);
    return manager ? manager.name : '—';
  };

  const getManagerOptions = () => {
    // Only show BM, AM, TL as manager options
    return users.filter(u => ['bm', 'am', 'tl'].includes(u.role));
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Users },
    { id: 'manage', label: 'Manage Users', icon: UserX },
    { id: 'create', label: 'Create User', icon: UserPlus },
    { id: 'bulk', label: 'Bulk Upload', icon: Upload },
    { id: 'approvals', label: 'Pending Approvals', icon: Clock, badge: pendingUsers.length },
    { id: 'audit', label: 'Audit Log', icon: FileText }
  ];

  const handleDeleteUser = async (userId) => {
    try {
      await axiosInstance.delete(`/admin/users/${userId}`);
      // Update state immediately to remove deleted user
      setUsers(prev => prev.filter(u => u.id !== userId));
      // Show success toast
      setToast({ message: 'User deleted successfully', type: 'success' });
      // Close modal
      setShowDeleteModal(false);
      setUserToDelete(null);
      // Refresh from server in background
      fetchUsers();
    } catch (error) {
      setToast({ message: error.response?.data?.detail || 'Failed to delete user', type: 'error' });
    }
  };

  const handleBulkDelete = async () => {
    try {
      await axiosInstance.post('/admin/users/bulk-delete', selectedUsers);
      // Update state immediately
      setUsers(prev => prev.filter(u => !selectedUsers.includes(u.id)));
      setToast({ message: `Deleted ${selectedUsers.length} users`, type: 'success' });
      setSelectedUsers([]);
      // Refresh from server in background
      fetchUsers();
    } catch (error) {
      setToast({ message: error.response?.data?.detail || 'Failed to delete users', type: 'error' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-background p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <h1 className="text-3xl font-bold text-textLight font-heading mb-2">
          Admin Dashboard
        </h1>
        <p className="text-textLight/60">Manage users, approvals, and system settings</p>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="card-light rounded-lg shadow-md p-2 flex gap-2 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-teal to-gold text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card-light rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Total Users</h3>
                <Users className="w-8 h-8 text-teal" />
              </div>
              <div className="text-4xl font-bold text-teal">{users.length}</div>
            </div>

            <div className="card-light rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Pending Approvals</h3>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
              <div className="text-4xl font-bold text-yellow-500">{pendingUsers.length}</div>
            </div>

            <div className="card-light rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Recent Actions</h3>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
              <div className="text-4xl font-bold text-blue-500">{auditLogs.length}</div>
            </div>

            {/* Recent Users */}
            <div className="md:col-span-3 card-light rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4">Recent Users</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-bold">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-bold">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-bold">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.slice(0, 10).map(user => (
                      <tr key={user.id} className="border-t border-gray-100">
                        <td className="px-4 py-3 text-sm font-medium">{user.name}</td>
                        <td className="px-4 py-3 text-sm font-medium">{user.email}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-teal/10 text-teal text-xs rounded-full font-semibold">
                            {user.role.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {user.approved ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <CheckCircle className="w-4 h-4" /> Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-yellow-600 text-sm">
                              <Clock className="w-4 h-4" /> Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Manage Users Tab */}
        {activeTab === 'manage' && (
          <div className="card-light rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Manage Users</h2>
              {selectedUsers.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Selected ({selectedUsers.length})
                </button>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === users.filter(u => u.id !== currentUser?.id && u.role !== 'admin').length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers(users.filter(u => u.id !== currentUser?.id && u.role !== 'admin').map(u => u.id));
                          } else {
                            setSelectedUsers([]);
                          }
                        }}
                        className="rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Role</th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Manager</th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => {
                    const isCurrentUser = user.id === currentUser?.id;
                    const isAdmin = user.role === 'admin';
                    const canDelete = !isCurrentUser && !isAdmin;
                    
                    return (
                      <tr key={user.id} className="border-t border-gray-100">
                        <td className="px-4 py-3">
                          {canDelete ? (
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUsers([...selectedUsers, user.id]);
                                } else {
                                  setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                }
                              }}
                              className="rounded"
                            />
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {user.name}
                          {isCurrentUser && <span className="ml-2 text-xs text-teal">(You)</span>}
                        </td>
                        <td className="px-4 py-3 text-sm">{user.email}</td>
                        <td className="px-4 py-3">
                          {editingUser === user.id ? (
                            <select
                              value={editForm.role}
                              onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="recruiter">Recruiter</option>
                              <option value="tl">Team Lead</option>
                              <option value="am">Area Manager</option>
                              <option value="bm">Branch Manager</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span className="px-2 py-1 bg-teal/10 text-teal text-xs rounded-full font-semibold">
                              {user.role.toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {editingUser === user.id ? (
                            <select
                              value={editForm.manager_id}
                              onChange={(e) => setEditForm({ ...editForm, manager_id: parseInt(e.target.value) })}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value={0}>No Manager</option>
                              {getManagerOptions().map(manager => (
                                <option key={manager.id} value={manager.id}>
                                  {manager.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-sm text-gray-700">
                              {getManagerName(user.manager_id)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {user.approved ? (
                            <span className="flex items-center text-green-600 text-sm">
                              <CheckCircle className="w-4 h-4 mr-1" /> Active
                            </span>
                          ) : (
                            <span className="flex items-center text-yellow-600 text-sm">
                              <Clock className="w-4 h-4 mr-1" /> Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {editingUser === user.id ? (
                              <>
                                <button
                                  onClick={() => handleSaveUser(user.id)}
                                  className="text-green-600 hover:text-green-800 transition"
                                  title="Save"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="text-gray-500 hover:text-gray-700 transition"
                                  title="Cancel"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                {!isCurrentUser && (
                                  <button
                                    onClick={() => handleEditUser(user)}
                                    className="text-blue-500 hover:text-blue-700 transition"
                                    title="Edit user"
                                  >
                                    <FileText className="w-4 h-4" />
                                  </button>
                                )}
                                {canDelete ? (
                                  <button
                                    onClick={() => {
                                      setUserToDelete(user);
                                      setShowDeleteModal(true);
                                    }}
                                    className="text-red-500 hover:text-red-700 transition"
                                    title="Delete user"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                ) : (
                                  !isCurrentUser && <span className="text-gray-300">—</span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create User Tab */}
        {activeTab === 'create' && (
          <div className="card-light rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Create New User</h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Full Name *</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Email *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Role *</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent"
                >
                  <option value="recruiter">Recruiter</option>
                  <option value="tl">Team Lead</option>
                  <option value="am">Account Manager</option>
                  <option value="bm">Branch Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Mobile</label>
                  <input
                    type="tel"
                    value={createForm.mobile}
                    onChange={(e) => setCreateForm({ ...createForm, mobile: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Location</label>
                  <input
                    type="text"
                    value={createForm.location}
                    onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <strong>Note:</strong> User will receive email with temporary password and must change it on first login.
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 bg-gradient-to-r from-teal to-gold text-white rounded-lg font-semibold hover:shadow-lg transition"
              >
                Create User
              </button>
            </form>
          </div>
        )}

        {/* Bulk Upload Tab */}
        {activeTab === 'bulk' && (
          <div className="card-light rounded-lg shadow-lg p-8 max-w-2xl mx-auto text-center">
            <Upload className="w-16 h-16 text-teal mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Bulk User Upload</h2>
            <p className="mb-6">
              Upload multiple users at once using CSV or Excel file
            </p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-8 py-3 bg-gradient-to-r from-teal to-gold text-white rounded-lg font-semibold hover:shadow-lg transition"
            >
              Start Bulk Upload
            </button>
          </div>
        )}

        {/* Pending Approvals Tab */}
        {activeTab === 'approvals' && (
          <div className="card-light rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-6">Pending Approvals</h2>
            {pendingUsers.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p>No pending approvals</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingUsers.map(user => (
                  <div key={user.id} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{user.name}</h3>
                      <p className="text-sm">{user.email}</p>
                      <span className="inline-block mt-2 px-2 py-1 bg-teal/10 text-teal text-xs rounded-full font-semibold">
                        {user.role.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApproval(user.id, true)}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" /> Approve
                      </button>
                      <button
                        onClick={() => handleApproval(user.id, false)}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit' && (
          <div className="card-light rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-6">Audit Log</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Time</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Actor</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Action</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Target</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-sm">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm">{log.actor_email}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold">
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{log.target_email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <UserUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          fetchUsers();
          fetchAuditLogs();
          setToast({ message: 'Bulk upload completed!', type: 'success' });
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete user <strong>{userToDelete.name}</strong> ({userToDelete.email})?
              This action cannot be undone.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(userToDelete.id)}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 ${toast.type === 'success' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'} border rounded-lg p-4 shadow-lg`}>
          <p className={toast.type === 'success' ? 'text-green-700' : 'text-red-700'}>{toast.message}</p>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
